import { useController, useFormContext } from 'react-hook-form';
import { useMemo } from 'react';
import {
  find,
  pipe,
  prop,
  propEq,
  slice,
  propOr,
  filter,
  map,
  concat,
  path,
  length,
  when,
  lte,
  __,
  and,
  not,
  equals,
  defaultTo,
  applySpec,
  unless,
  isNil
} from 'ramda';

export const REGIME_CODE_INDEXES = { FROM: 0, TO: 2 };
export const FUND_CODE_INDEXES = { FROM: 2, TO: 5 };
export const CENTER_CODE_INDEXES = { FROM: 5, TO: 10 };
const CENTER_CODE_MAX_LENGTH = 4;
const ORGANISM_CODE_LENGTH = 9;

const sliceOrganismCode = (from, to, organismCode) => unless(isNil, slice(from, to), organismCode);

const useControlledSocialSecurityOrganismCode = ({
  rules,
  name,
  regimeName,
  regimeObligatoire
}) => {
  const { watch, control } = useFormContext();

  const formikProps = useController({
    name,
    control,
    rules: prop('required', rules) ? { validate: pipe(length, equals(ORGANISM_CODE_LENGTH)) } : {}
  });

  const regime = watch(regimeName);
  const organismCode = watch(name);

  const regimeCode = useMemo(
    () => sliceOrganismCode(REGIME_CODE_INDEXES.FROM, REGIME_CODE_INDEXES.TO, organismCode),
    [organismCode]
  );
  const fundCode = useMemo(
    () => sliceOrganismCode(FUND_CODE_INDEXES.FROM, FUND_CODE_INDEXES.TO, organismCode),
    [organismCode]
  );
  const centerCode = useMemo(
    () => sliceOrganismCode(CENTER_CODE_INDEXES.FROM, CENTER_CODE_INDEXES.TO, organismCode),
    [organismCode]
  );

  const regimeCodeOptions = pipe(
    propOr([], 'regimeCodes'),
    filter(propEq('regime', regime)),
    map(applySpec({ label: prop('value'), value: prop('value') }))
  )(regimeObligatoire);

  const fundCodeOptions = pipe(
    propOr([], 'caisseCodes'),
    filter(propEq('regimeCode', regimeCode)),
    map(applySpec({ label: prop('value'), value: prop('value') }))
  )(regimeObligatoire);

  const findOrNull = (value, array) => pipe(find(propEq('value', value)), defaultTo(null))(array);
  const selectedRegimeCodeOption = findOrNull(regimeCode, regimeCodeOptions);
  const selectedFundCodeOption = findOrNull(fundCode, fundCodeOptions);

  const hasValidCenterCodeLength = pipe(length, lte(__, CENTER_CODE_MAX_LENGTH));
  const concatWithRegimeCodeAndFundCode = concat(
    sliceOrganismCode(REGIME_CODE_INDEXES.FROM, FUND_CODE_INDEXES.TO, organismCode)
  );
  const onCenterCodeChange = pipe(
    path(['target', 'value']),
    when(
      (value) => and(hasValidCenterCodeLength(value), not(isNaN(value))),
      pipe(concatWithRegimeCodeAndFundCode, formikProps.field.onChange)
    )
  );

  const isCenterCodeLengthCorrect = pipe(length, equals(CENTER_CODE_MAX_LENGTH));
  const hasCenterCodeError = and(
    selectedFundCodeOption,
    not(isCenterCodeLengthCorrect(centerCode))
  );

  return {
    formikProps,
    onCenterCodeChange,
    hasCenterCodeError,
    selectedRegimeCodeOption,
    selectedFundCodeOption,
    regimeCodeOptions,
    fundCodeOptions,
    regime,
    regimeCode,
    fundCode,
    centerCode
  };
};

export default useControlledSocialSecurityOrganismCode;
